// src/services/publicProductService.js
import { db } from "../firebaseConfig";
import {
  collection,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";

/* ---------------- Helpers ---------------- */
function normalize(str = "") {
  return String(str)
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}\s.'-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function tokenize(str = "") {
  const s = normalize(str);
  if (!s) return [];
  const parts = s.split(" ").filter((w) => w.length >= 2);
  return Array.from(new Set(parts));
}

function isInStock(p) {
  // si champ existe
  if (typeof p?.inStock === "boolean") return p.inStock;

  // sinon calcule (produit simple / variantes)
  const hasVariants = Array.isArray(p?.variants) && p.variants.length > 0;
  if (hasVariants) return p.variants.some((v) => Number(v?.stock || 0) > 0);
  return Number(p?.stock || 0) > 0;
}

function safeOrder(sort) {
  // évite createdAt si tes docs ne l’ont pas
  if (sort === "price_asc") return { field: "price", dir: "asc" };
  if (sort === "price_desc") return { field: "price", dir: "desc" };
  // fallback le plus “universel”
  return { field: "title", dir: "asc" };
}

/* ---------------- API ---------------- */
export async function getProductBySlug(slug) {
  try {
    const snap = await getDocs(
      query(
        collection(db, "products"),
        where("slug", "==", slug),
        where("status", "==", "active"),
        fsLimit(1)
      )
    );
    if (snap.empty) return null;
    const d = snap.docs[0];
    const p = { id: d.id, ...d.data() };
    return isInStock(p) ? p : null; // ✅ jamais hors stock
  } catch (e) {
    console.error("[getProductBySlug]", e);
    return null;
  }
}

/**
 * listActiveProducts SAFE:
 * 1) essaie: status + inStock + tokens + tri
 * 2) fallback: status + tri (sans tokens/inStock)
 * puis filtre stock + recherche côté client
 */
export async function listActiveProducts({
  search = "",
  brandId = "",
  categoryId = "",
  sort = "new",
  pageSize = 12,
  cursor = null,
  onlyInStock = "1",
  maxPagesWhenSearching = 6, // ✅ pour éviter de charger trop
} = {}) {
  const base = collection(db, "products");
  const sNorm = normalize(search);
  const tokens = tokenize(search).slice(0, 8);
  const { field, dir } = safeOrder(sort);

  const makeBaseClauses = () => {
    const cs = [where("status", "==", "active")];
    if (brandId) cs.push(where("brandId", "==", brandId));
    if (categoryId) cs.push(where("categoryId", "==", categoryId));
    return cs;
  };

  // ✅ tentatives (du plus strict au plus compatible)
  const attempts = [];

  // 1) inStock + tokens
  if (tokens.length) {
    const cs = makeBaseClauses();
    if (onlyInStock === "1") cs.push(where("inStock", "==", true));
    cs.push(where("tokens", "array-contains-any", tokens));
    cs.push(orderBy(field, dir), fsLimit(pageSize));
    attempts.push({ clauses: cs, name: "inStock+tokens" });
  }

  // 2) tokens only
  if (tokens.length) {
    const cs = makeBaseClauses();
    cs.push(where("tokens", "array-contains-any", tokens));
    cs.push(orderBy(field, dir), fsLimit(pageSize));
    attempts.push({ clauses: cs, name: "tokens" });
  }

  // 3) inStock only
  {
    const cs = makeBaseClauses();
    if (onlyInStock === "1") cs.push(where("inStock", "==", true));
    cs.push(orderBy(field, dir), fsLimit(pageSize));
    attempts.push({ clauses: cs, name: "inStock" });
  }

  // 4) ultra compatible
  {
    const cs = makeBaseClauses();
    cs.push(orderBy(field, dir), fsLimit(pageSize));
    attempts.push({ clauses: cs, name: "base" });
  }

  const runQuery = async (clauses, cur) => {
    let q = query(base, ...clauses);
    if (cur) q = query(q, startAfter(cur));
    const snap = await getDocs(q);
    return snap;
  };

  // ✅ 1) choisir la meilleure requête (mais NE PAS s’arrêter si 0 résultat)
  let chosenSnap = null;
  let chosenAttempt = null;
  let lastError = null;

  for (const a of attempts) {
    try {
      const snap = await runQuery(a.clauses, cursor);
      if (snap.size > 0 || a.name === "base") {
        chosenSnap = snap;
        chosenAttempt = a.name;
        break;
      }
      // si vide -> on essaie la tentative suivante
    } catch (e) {
      lastError = e;
      // erreur (index manquant etc.) -> on continue
    }
  }

  if (!chosenSnap) {
    console.error("[listActiveProducts] all queries failed:", lastError);
    return { items: [], nextCursor: null };
  }

  let items = chosenSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // ✅ filtre stock partout (même si inStock n’existe pas)
  if (onlyInStock === "1") items = items.filter(isInStock);

  // ✅ filtre search côté client
  const filterClient = (arr) => {
    if (!sNorm) return arr;
    return arr.filter((p) => {
      const title = normalize(p?.title || "");
      const code = normalize(p?.code || p?.reference || "");
      const extra = Array.isArray(p?.search) ? p.search.map(normalize).join(" ") : "";
      return title.includes(sNorm) || code.includes(sNorm) || extra.includes(sNorm);
    });
  };

  items = filterClient(items);

  // ✅ 2) si search actif et pas assez de résultats, on “page” un peu plus (progressif)
  // Ça règle le cas: limit(12) puis filtre => 0 résultats.
  let nextCursor = chosenSnap.docs.at(-1) || null;

  if (sNorm && items.length < pageSize) {
    let pages = 1;
    let cur = nextCursor;
    let collected = [...items];

    while (pages < maxPagesWhenSearching && cur) {
      pages += 1;

      const snap2 = await runQuery(
        // on relance la même tentative choisie (ou base)
        (attempts.find((x) => x.name === chosenAttempt) || attempts.at(-1)).clauses,
        cur
      );

      if (snap2.empty) {
        cur = null;
        break;
      }

      let batch = snap2.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (onlyInStock === "1") batch = batch.filter(isInStock);
      batch = filterClient(batch);

      // dédup
      const seen = new Set(collected.map((x) => x.id || x.slug));
      for (const p of batch) {
        const k = p?.id || p?.slug;
        if (!k || seen.has(k)) continue;
        seen.add(k);
        collected.push(p);
        if (collected.length >= pageSize) break;
      }

      cur = snap2.docs.at(-1) || null;
      nextCursor = cur;

      if (collected.length >= pageSize) break;
    }

    items = collected.slice(0, pageSize);
  }

  return { items, nextCursor };
}

export async function listPublicBrands() {
  const snap = await getDocs(query(collection(db, "brands"), orderBy("createdAt", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listPublicCategories() {
  const snap = await getDocs(query(collection(db, "categories"), orderBy("createdAt", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listVisibleCategories() {
  const snap = await getDocs(query(collection(db, "categories"), where("visible", "==", true)));
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  items.sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""), "fr", { sensitivity: "base" })
  );
  return items;
}
